import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DialogService } from '../../../../shared/services/dialog.service';
import { ValidationRulesFacade } from '../../facade/validation-rules.facade';
import {
  ICrossFieldRule,
  ICrossFieldRulePatch,
  IValidatorRule,
  IValidatorRulePatch,
} from '../../models/validation-rule';
import { DeleteRuleModalComponent } from '../../modals/delete-rule-modal/delete-rule-modal.component';
import { RuleEditorModalComponent } from '../../modals/rule-editor-modal/rule-editor-modal.component';

/** Tagged union of rules so the delete modal can handle both kinds. */
type RuleToDelete =
  | { kind: 'field'; rule: IValidatorRule }
  | { kind: 'cross'; rule: ICrossFieldRule };

/** Modal-open intent: which kind to edit and (for edit) the existing rule. */
type EditorMode =
  | { kind: 'field'; rule: IValidatorRule | null }
  | { kind: 'cross'; rule: ICrossFieldRule | null }
  | null;

/**
 * Right pane: shows the field-rules and cross-field-rules tables for the
 * currently selected template. Empty state when nothing is selected.
 *
 * Edit / create actions delegate to `RuleEditorModalComponent`; deletes
 * delegate to `DeleteRuleModalComponent`. Inline `is_active` toggle
 * dispatches a PATCH directly so the user gets immediate feedback without
 * opening the modal.
 */
@Component({
  selector: 'app-template-rules-panel',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, RuleEditorModalComponent, DeleteRuleModalComponent],
  templateUrl: './template-rules-panel.component.html',
})
export class TemplateRulesPanelComponent {
  private facade = inject(ValidationRulesFacade);
  private dialog = inject(DialogService);

  selectedTemplateId = this.facade.selectedTemplateIdSignal;
  fieldRules = this.facade.fieldRulesForSelectedTemplateSignal;
  crossFieldRules = this.facade.crossFieldRulesForSelectedTemplateSignal;
  catalog = this.facade.catalogSignal;

  editorModalId = 'validation-rule-editor-modal';
  deleteModalId = 'validation-rule-delete-modal';

  editorMode: EditorMode = null;
  pendingDelete: RuleToDelete | null = null;

  // Used by the field-rules table for stable display ordering. The backend
  // already sorts by `sort_order`, but we tolerate out-of-order arrays.
  sortedFieldRules = computed(() =>
    [...this.fieldRules()].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id),
  );

  sortedCrossFieldRules = computed(() =>
    [...this.crossFieldRules()].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id),
  );

  // ---- editor open/close ------------------------------------------

  openCreate(kind: 'field' | 'cross'): void {
    this.editorMode = { kind, rule: null } as EditorMode;
    this.dialog.open(this.editorModalId);
  }

  openEdit(rule: IValidatorRule | ICrossFieldRule, kind: 'field' | 'cross'): void {
    // The cast keeps the union tagged correctly without sprinkling
    // assertions through the template.
    this.editorMode =
      kind === 'field'
        ? { kind: 'field', rule: rule as IValidatorRule }
        : { kind: 'cross', rule: rule as ICrossFieldRule };
    this.dialog.open(this.editorModalId);
  }

  onEditorClosed(): void {
    this.editorMode = null;
    this.dialog.close(this.editorModalId);
  }

  // ---- inline toggle ----------------------------------------------

  toggleFieldActive(rule: IValidatorRule): void {
    const changes: IValidatorRulePatch = { is_active: !rule.is_active };
    this.facade.patchFieldRule(rule.id, changes);
  }

  toggleCrossActive(rule: ICrossFieldRule): void {
    const changes: ICrossFieldRulePatch = { is_active: !rule.is_active };
    this.facade.patchCrossFieldRule(rule.id, changes);
  }

  // ---- delete -----------------------------------------------------

  beginDelete(rule: IValidatorRule | ICrossFieldRule, kind: 'field' | 'cross'): void {
    this.pendingDelete =
      kind === 'field'
        ? { kind: 'field', rule: rule as IValidatorRule }
        : { kind: 'cross', rule: rule as ICrossFieldRule };
    this.dialog.open(this.deleteModalId);
  }

  confirmDelete(): void {
    if (!this.pendingDelete) return;
    if (this.pendingDelete.kind === 'field') {
      this.facade.deleteFieldRule(this.pendingDelete.rule.id);
    } else {
      this.facade.deleteCrossFieldRule(this.pendingDelete.rule.id);
    }
    this.cancelDelete();
  }

  cancelDelete(): void {
    this.pendingDelete = null;
    this.dialog.close(this.deleteModalId);
  }

  // ---- display helpers --------------------------------------------

  /** Short, one-line representation of a rule's params for the table. */
  paramsSummary(params: Record<string, unknown>): string {
    if (!params || Object.keys(params).length === 0) return '—';
    return Object.entries(params)
      .map(([k, v]) => `${k}=${this.formatValue(v)}`)
      .join(', ');
  }

  private formatValue(v: unknown): string {
    if (Array.isArray(v)) {
      if (v.length <= 3) return `[${v.join(', ')}]`;
      return `[${v.slice(0, 3).join(', ')}, …+${v.length - 3}]`;
    }
    if (typeof v === 'string' && v.length > 40) return `"${v.slice(0, 37)}…"`;
    return JSON.stringify(v);
  }

  trackRuleId(_: number, r: { id: number }): number {
    return r.id;
  }
}
